import { analyticsEvents } from "@healthos/analytics";
import {
  type BroadcastModalProps,
  broadcastModalProps,
  broadcastPrimaryAction,
  recordMomentOutcome,
  reportMomentSeen,
} from "@healthos/client-data";
import { Modal } from "@healthos/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { client } from "../../api/healthos";
import { useAttentionEvents } from "../../hooks/use-attention-events";
import { useDayZeroRunActive } from "../../hooks/use-day-zero-tour";
import { analytics } from "../../lib/analytics";
import { createDesktopExternalOpener, getActiveHostAuth } from "../../lib/host-auth";
import { broadcastMicrostepProperties } from "../../pages/ambient/microstep-analytics";
import { useBoot } from "../boot/boot-context";
import { BroadcastCard } from "./broadcast-card";

// window.open doesn't work inside the MS Teams embed; use createDesktopExternalOpener instead.
export function openBroadcastLink(url: string): void {
  const hostAuth = getActiveHostAuth();
  if (hostAuth) {
    createDesktopExternalOpener(hostAuth)(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

const HELD_ROUTES = /^\/(onboarding|operations)(\/|$)/;

/** Shows the oldest unanswered Heartbeat broadcast, one at a time. */
export function BroadcastHost() {
  const location = useLocation();
  const navigate = useNavigate();
  const boot = useBoot();
  const dayZeroRunActive = useDayZeroRunActive();
  const { data } = useAttentionEvents();
  const [answered, setAnswered] = useState<ReadonlySet<string>>(() => new Set());

  const current = useMemo<BroadcastModalProps | null>(() => {
    for (const event of data?.events ?? []) {
      const props = broadcastModalProps(event);
      if (props && !answered.has(props.momentId)) return props;
    }
    return null;
  }, [data?.events, answered]);

  const held = boot.visible || dayZeroRunActive || HELD_ROUTES.test(location.pathname);
  const showing = current && !held ? current : null;
  const momentId = showing?.momentId ?? null;

  useEffect(() => {
    if (!momentId) return;
    void reportMomentSeen(client(), momentId).catch(() => undefined);
  }, [momentId]);

  const answer = (outcome: "acted" | "dismissed") => {
    if (!showing) return;
    setAnswered((prev) => new Set(prev).add(showing.momentId));
    const primaryAction = broadcastPrimaryAction(showing);
    void recordMomentOutcome(client(), showing.momentId, outcome)
      .then(() => {
        if (outcome === "acted") {
          analytics.track(analyticsEvents.microstepCompleted, {
            ...broadcastMicrostepProperties(),
            action: primaryAction,
          });
        } else {
          analytics.track(analyticsEvents.microstepDismissed, broadcastMicrostepProperties());
        }
      })
      .catch(() => undefined);
  };

  // A push tap already opened what the broadcast points at: that is the answer.
  const onTarget =
    showing?.contentId != null &&
    location.pathname === "/explore" &&
    new URLSearchParams(location.search).get("content") === showing.contentId;
  useEffect(() => {
    if (!onTarget || !momentId) return;
    setAnswered((prev) => new Set(prev).add(momentId));
    void recordMomentOutcome(client(), momentId, "acted").catch(() => undefined);
  }, [onTarget, momentId]);

  // Shown means the card was on screen, once per broadcast: a hold that clears
  // and returns, or a push that already opened the target, is not a new view.
  const trackedShownRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!momentId || onTarget || trackedShownRef.current.has(momentId)) return;
    trackedShownRef.current.add(momentId);
    analytics.track(analyticsEvents.microstepShown, broadcastMicrostepProperties());
  }, [momentId, onTarget]);

  if (!showing || onTarget) return null;
  return (
    <Modal open onOpenChange={(open) => !open && answer("dismissed")} size="sm">
      <BroadcastCard
        broadcast={showing}
        onPrimary={() => {
          answer("acted");
          if (!showing.target) return;
          if (showing.external) openBroadcastLink(showing.target);
          else navigate(showing.target);
        }}
        onDismiss={() => answer("dismissed")}
      />
    </Modal>
  );
}
